import { useQuery } from "@tanstack/react-query";
import { request } from "./client";

/**
 * Hisobot natijasi (reporting-service).
 *
 * Barcha qiymat MATN sifatida keladi (dev_spec.md §7.2): miqdorlar
 * JSON float bo'lsa yaxlitlash xatosi qoldiqni buzardi. Shuning
 * uchun interfeys ham ularni matn sifatida ushlaydi va faqat
 * ko'rsatishdan oldin formatlaydi.
 */
export interface ReportResult {
  report: string;
  columns: string[];
  items: Array<Record<string, string>>;
  has_more: boolean;
}

export type ReportName =
  | "stock"
  | "location-capacity"
  | "tasks"
  | "discrepancies"
  | "incomplete-orders"
  | "kpi"
  | "data-quality"
  | "abc-xyz"
  // TZ 3.0 RPT-03
  | "customs-stock"
  | "sku-without-ox"
  | "abc-zone-mismatch"
  | "lp-misplaced"
  | "slow-moving";

export interface ReportFilters {
  warehouse_code?: string;
  zone_code?: string;
  location_function?: string;
  status?: string;
  task_type?: string;
  process?: string;
  sku_id?: string;
  location_id?: string;
  assignee_id?: string;
  order_id?: string;
  from?: string;
  to?: string;
  only_issues?: boolean;
  /** `abc-xyz`: ABC kesimi (A, B, C). */
  abc_class?: string;
  /** `abc-xyz`: XYZ kesimi (X, Y, Z yoki NONE — klass berilmagan). */
  xyz_class?: string;
  /** `slow-moving`: harakatsizlik chegarasi (30/60/90 kun). */
  idle_days?: number;
  /** `lp-misplaced`: oraliq hududda qotish chegarasi (soat). */
  stuck_hours?: number;
  limit?: number;
}

export function reportQueryKey(report: ReportName, filters: ReportFilters) {
  return ["report", report, filters] as const;
}

export function useReport(
  report: ReportName,
  filters: ReportFilters = {},
  enabled = true,
) {
  return useQuery({
    queryKey: reportQueryKey(report, filters),
    enabled,
    queryFn: ({ signal }) =>
      request<ReportResult>("reporting", `/api/v1/reports/${report}`, {
        query: { ...filters, limit: filters.limit ?? 100 },
        signal,
      }),
  });
}

/**
 * Proyeksiya holati.
 *
 * Hisobot CQRS read model: u manbadan biroz ORQADA qoladi.
 * Kechikish interfeysda ochiq ko'rsatiladi — hisobotni ko'rgan
 * odam ma'lumot qanchalik yangi ekanini bilishi kerak.
 */
export interface ProjectionState {
  projection: string;
  last_event_type: string;
  last_event_at: string;
  applied_count: number;
  skipped_count: number;
  last_error: string;
  updated_at: string;
}

export function useProjectionStatus() {
  return useQuery({
    queryKey: ["reports-status"],
    // Oqim (SSE) ishlaganda kechikish o'zi yangilanadi; bu
    // interval ZAXIRA: oqim uzilib qolsa ham ko'rsatkich
    // muzlab qolmasligi kerak.
    refetchInterval: 60_000,
    queryFn: ({ signal }) =>
      request<{ items: ProjectionState[] }>("reporting", "/api/v1/reports-status", {
        signal,
      }),
  });
}

/**
 * ABC × XYZ matritsasi (TZ 3.0 P04-02).
 *
 * Katak — SKU SONI. Katak ichidagi ro'yxat umumiy `abc-xyz`
 * hisobotidan olinadi: jadval 200 satr bilan cheklangan va
 * matritsani undan sanab bo'lmaydi.
 */
export interface AbcXyzCell {
  abc_class: string;
  /** "NONE" — XYZ klassi berilmagan (sababi `xyz_note`). */
  xyz_class: string;
  xyz_note?: string;
  sku_count: number;
}

/** Klass qaysi chegara bilan hisoblangani — ulushlar matn. */
export interface ClassificationRule {
  run_id?: number;
  run_at?: string;
  abc_window_days: number;
  abc_thresholds: { a: string; b: string };
  xyz_period_days: number;
  xyz_periods: number;
  xyz_thresholds: { x: string; y: string };
}

export interface AbcXyzMatrix {
  cells: AbcXyzCell[];
  rule: ClassificationRule | null;
}

export function useAbcXyzMatrix() {
  return useQuery({
    queryKey: ["abc-xyz-matrix"],
    queryFn: ({ signal }) =>
      request<AbcXyzMatrix>("reporting", "/api/v1/reports/abc-xyz/matrix", { signal }),
  });
}
